/* import __COLOCATED_TEMPLATE__ from './ai-agent-identities-editor.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import ajax, { get } from 'embercom/lib/ajax';
import { type TaskGenerator } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
//@ts-expect-error
import DefaultAvatars from '@intercom/pulse/lib/default-avatars';
import type IntlService from 'ember-intl/services/intl';
import type FinSuffixSettings from 'embercom/models/fin-suffix-settings';

interface AiAgentIdentity {
  name: string;
  avatar: {
    imageUrls: {
      square25: string;
      square50: string;
      square128: string;
    };
    type: string;
  };
  isDefault: boolean;
  finSuffixSettings?: FinSuffixSettings;
}

interface BrandAiAgentIdentityWireFormat {
  brand_id: string;
  brand_name: string;
  is_default_brand: boolean;
  ai_agent_identity: {
    name: string;
    avatar: {
      image_urls: {
        square_25: string;
        square_50: string;
        square_128: string;
      };
      type: string;
    };
    is_default: boolean;
  };
}

interface UnsavedChanges {
  brandId: string;
  aiAgentIdentity: {
    name?: string;
    avatarUpload?: File;
    avatarType?: string;
  };
}

class BrandAiAgentIdentity {
  @tracked brandId: string;
  @tracked brandName: string;
  @tracked isDefaultBrand: boolean;
  @tracked aiAgentIdentity: AiAgentIdentity;

  constructor(
    brandId: string,
    brandName: string,
    isDefaultBrand: boolean,
    aiAgentIdentity: AiAgentIdentity,
  ) {
    this.brandId = brandId;
    this.brandName = brandName;
    this.isDefaultBrand = isDefaultBrand;
    this.aiAgentIdentity = aiAgentIdentity;
  }

  static deserialize(data: BrandAiAgentIdentityWireFormat): BrandAiAgentIdentity {
    return new BrandAiAgentIdentity(data.brand_id, data.brand_name, data.is_default_brand, {
      name: data.ai_agent_identity.name,
      avatar: {
        imageUrls: {
          square25: data.ai_agent_identity.avatar.image_urls.square_25,
          square50: data.ai_agent_identity.avatar.image_urls.square_50,
          square128: data.ai_agent_identity.avatar.image_urls.square_128,
        },
        type: data.ai_agent_identity.avatar.type,
      },
      isDefault: data.ai_agent_identity.is_default,
    });
  }
}

export default class AiAgentIdentitiesEditor extends Component {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare router: $TSFixMe;
  @tracked finSuffixSettings: FinSuffixSettings | undefined = undefined;
  @service declare store: $TSFixMe;

  @tracked hasUnsavedChanges = false;
  @tracked unsavedChanges: Record<string, UnsavedChanges> = {};
  brandIdToIdentityMap: Record<string, BrandAiAgentIdentity> = {};

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.loadFinSuffixSettings();
  }

  @action
  async loadFinSuffixSettings() {
    try {
      this.finSuffixSettings = await this.store.findRecord(
        'fin-suffix-settings',
        this.appService.app.id,
      );
    } catch (error) {
      console.error('Failed to load fin suffix settings:', error);
    }
  }

  @use private brandAiAgentIdentitiesLoader = AsyncData<BrandAiAgentIdentity[]>(async () => {
    let brandIdentities = await get(`/ember/ai_agent/identities?app_id=${this.appService.app.id}`);
    brandIdentities = brandIdentities.map((brandIdentity: BrandAiAgentIdentityWireFormat) =>
      BrandAiAgentIdentity.deserialize(brandIdentity),
    );
    // Build a map of brandId to BrandAiAgentIdentity for quick lookup
    this.brandIdToIdentityMap = brandIdentities.reduce(
      (acc: Record<string, BrandAiAgentIdentity>, brandIdentity: BrandAiAgentIdentity) => {
        acc[brandIdentity.brandId] = brandIdentity;
        return acc;
      },
      {} as Record<string, BrandAiAgentIdentity>,
    );
    return brandIdentities;
  });

  get columns() {
    return [
      {
        label: this.intl.t('ai-agent.identity.ai-agent-identities-editor.brand-name'),
        valuePath: 'brandName',
      },
      {
        label: this.intl.t('ai-agent.identity.ai-agent-identities-editor.profile'),
        valuePath: 'aiAgentIdentity.avatar',
      },
      {
        label: this.intl.t('ai-agent.identity.ai-agent-identities-editor.name'),
        valuePath: 'aiAgentIdentity.name',
      },
    ];
  }

  get identities() {
    return this.brandAiAgentIdentitiesLoader.value?.map((brandIdentity) => {
      return {
        brandId: brandIdentity.brandId,
        brandName: brandIdentity.brandName,
        isDefaultBrand: brandIdentity.isDefaultBrand,
        aiAgentIdentity: {
          name: brandIdentity.aiAgentIdentity.name,
          isDefault: brandIdentity.aiAgentIdentity.isDefault,
          avatar: {
            avatarType: brandIdentity.aiAgentIdentity.avatar.type,
            avatarData: {
              avatarShape: 'squircle',
              url: brandIdentity.aiAgentIdentity.avatar.imageUrls.square128,
            },
          },
        },
      };
    });
  }

  get defaultAvatarUrl() {
    return DefaultAvatars.fin[128];
  }

  get savingIdentities() {
    return taskFor(this.saveIdentities).isRunning;
  }

  @task({ restartable: true })
  *saveIdentities(): TaskGenerator<void> {
    let formData = new FormData();
    formData.append('app_id', this.appService.app.id);
    Object.values(this.unsavedChanges).forEach((unsavedChange) => {
      formData.append(
        `brand_identities[${unsavedChange.brandId}][brand_id]`,
        unsavedChange.brandId,
      );
      formData.append(
        `brand_identities[${unsavedChange.brandId}][ai_agent_identity][name]`,
        unsavedChange.aiAgentIdentity.name ??
          this.brandIdToIdentityMap[unsavedChange.brandId].aiAgentIdentity.name,
      );

      formData.append(
        `brand_identities[${unsavedChange.brandId}][ai_agent_identity][avatar_type]`,
        unsavedChange.aiAgentIdentity.avatarType ??
          this.brandIdToIdentityMap[unsavedChange.brandId].aiAgentIdentity.avatar.type,
      );

      formData.append(
        `brand_identities[${unsavedChange.brandId}][ai_agent_identity][avatar_upload]`,
        unsavedChange.aiAgentIdentity.avatarUpload ?? '',
      );
    });

    yield ajax({
      type: 'POST',
      url: '/ember/ai_agent/identities/bulk_save',
      data: formData,
      contentType: false,
      processData: false,
    });

    this.resetChanges();
  }

  @action
  resetChanges() {
    this.unsavedChanges = {};
    this.brandAiAgentIdentitiesLoader.reload();
    this.hasUnsavedChanges = false;
  }

  @action
  async onAvatarChange(file: File, brandId: string) {
    let unsavedChanges = this.getUnsavedChanges(brandId);
    unsavedChanges.aiAgentIdentity.avatarUpload = file;
    unsavedChanges.aiAgentIdentity.avatarType = 'custom';
    this.hasUnsavedChanges = true;
  }

  @action
  onAvatarResetToDefault(brandId: string) {
    let unsavedChanges = this.getUnsavedChanges(brandId);
    unsavedChanges.aiAgentIdentity.avatarUpload = undefined;
    unsavedChanges.aiAgentIdentity.avatarType = 'default';
    this.hasUnsavedChanges = true;
  }

  @action
  onNameChange(brandId: string, event: Event) {
    let unsavedChanges = this.getUnsavedChanges(brandId);
    unsavedChanges.aiAgentIdentity.name = (event.target as HTMLInputElement).value;
    this.hasUnsavedChanges = true;
  }

  @action
  onManageBrandsClick() {
    this.router.transitionTo('apps.app.settings.workspace.brands', this.appService.app.id);
  }

  private getUnsavedChanges(brandId: string) {
    if (!this.unsavedChanges[brandId]) {
      this.unsavedChanges[brandId] = {
        brandId,
        aiAgentIdentity: {
          name: this.brandIdToIdentityMap[brandId].aiAgentIdentity.name,
        },
      };
    }
    return this.unsavedChanges[brandId];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Identity::AiAgentIdentitiesEditor': typeof AiAgentIdentitiesEditor;
    'ai-agent/identity/ai-agent-identities-editor': typeof AiAgentIdentitiesEditor;
  }
}
